import { pageNumberState } from "@/atoms/order/page-number-state";
import { ButtonColor } from "@/common/enum/button-color";
import { PaginationSize } from "@/common/enum/pagination-size";
import { GetAdminPagePayload } from "@/types/payload/get-admin-page-payload";
import { Button, Modal, TextInput } from "flowbite-react";
import React, { useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { toastState } from "../../atoms/system/toast-state";
import { gradePageState } from "../../atoms/user/grade-page-state";
import { gradeService } from "../../services/grade-service";
import { GradeDto } from "../../types/dto/grade-dto";
import { UpdateGradePayload } from "../../types/payload/update-grade-payload";
import { CustomHelperText } from "../CustomHelperText";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Regular } from "../typography/Body14Regular";
import { Body16Regular } from "../typography/Body16Regular";
import { H4 } from "../typography/H4";
import { H5 } from "../typography/H5";

interface Props {
  selectedTier?: GradeDto;
  closeModal: () => void;
  getPage: (input: GetAdminPagePayload) => void;
}

export const PlatformTierModal: React.FC<Props> = ({
  selectedTier,
  closeModal,
  getPage,
}) => {
  const [pageNumber, setPageNumber] = useRecoilState(pageNumberState);
  const [payload, setPayload] = useState<UpdateGradePayload>({
    name: selectedTier?.name || "",
    requirement: selectedTier ? String(selectedTier.requirement) : "",
    discountRate: selectedTier ? String(selectedTier.discountRate) : "",
  });
  const [aggregateErrors, setAggregateErrors] = useState({
    name: "",
    requirement: "",
    discountRate: "",
  });

  const [page, setPage] = useRecoilState(gradePageState);
  const setToast = useSetRecoilState(toastState);

  const onChangePayload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "discountRate") {
      const valid = /^(\d{1,2})?$/.test(value);
      if (!valid) {
        return;
      }
    }

    if (name === "requirement") {
      const valid = /^\d+$/.test(value);
      if (!valid) {
        return;
      }
    }

    setPayload({ ...payload, [name]: value });
  };

  const handleCreateClick = async () => {
    const response = await gradeService.create({
      name: payload.name,
      requirement: payload.requirement,
      discountRate: payload.discountRate,
    });
    setAggregateErrors({
      name: response.data.aggregateErrors?.name || "",
      requirement: response.data.aggregateErrors?.requirement || "",
      discountRate: response.data.aggregateErrors?.discountRate || "",
    });
    if (response.ok) {
      setPage((prev) => ({
        ...prev,
        contents: [response.data, ...page.contents],
      }));
      setToast({
        show: true,
        messages: [`등급을 추가했습니다.`],
      });
      closeModal();
    }
  };

  const handleUpdateClick = async () => {
    const updateByIdResponse = await gradeService.updateById(selectedTier!.id, {
      name: payload.name,
      requirement: payload.requirement,
      discountRate: payload.discountRate,
    });
    setAggregateErrors({
      name: updateByIdResponse.data.aggregateErrors?.name || "",
      requirement: updateByIdResponse.data.aggregateErrors?.requirement || "",
      discountRate: updateByIdResponse.data.aggregateErrors?.discountRate || "",
    });
    if (updateByIdResponse.ok) {
      const payload = {
        page: pageNumber,
        size: PaginationSize.SIZE_12,
      };
      getPage(payload);
      setToast({
        show: true,
        messages: [`등급을 수정했습니다.`],
      });
      closeModal();
    }
  };

  const handleDeleteClick = async () => {
    const response = await gradeService.deleteById(selectedTier!.id);
    if (!response.ok) {
      return;
    }
    setPage((prev) => ({
      ...prev,
      contents: prev.contents.filter((item) => item.id !== selectedTier!.id),
    }));
    setToast({
      show: true,
      messages: [`등급을 삭제했습니다.`],
    });
    closeModal();
  };

  const disabled =
    !payload.name || !payload.requirement || !payload.discountRate;

  return (
    <Modal size="2xl" show={true} onClose={closeModal}>
      <Modal.Header className="p-[24px] h-[76px]">
        <H4>{selectedTier ? "등급 수정" : "등급 추가"}</H4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex justify-center items-center">
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col">
              <TextInputLabel required>등급명</TextInputLabel>
              <TextInput
                required
                className="w-[572px]"
                placeholder="등급명을 입력해주세요"
                name="name"
                value={payload.name}
                onChange={onChangePayload}
              />
              {!!aggregateErrors.name && (
                <CustomHelperText
                  isError={!!aggregateErrors.name}
                  text={aggregateErrors.name}
                />
              )}
            </div>
            <div className="flex flex-col">
              <TextInputLabel required>등업 기준</TextInputLabel>
              <div className="flex gap-[8px] items-center">
                <TextInput
                  required
                  className="w-[473px]"
                  placeholder="숫자만 입력해주세요"
                  name="requirement"
                  value={payload.requirement}
                  onChange={onChangePayload}
                />
                <Body16Regular>원 이상 실결제</Body16Regular>
              </div>
              {!!aggregateErrors.requirement && (
                <CustomHelperText
                  isError={!!aggregateErrors.requirement}
                  text={aggregateErrors.requirement}
                />
              )}
            </div>
            <div className="flex flex-col">
              <TextInputLabel required>상품 할인</TextInputLabel>
              <TextInput
                required
                className="w-[572px]"
                placeholder="숫자만 입력해주세요"
                name="discountRate"
                value={payload.discountRate}
                onChange={onChangePayload}
              />
              {!!aggregateErrors.discountRate && (
                <CustomHelperText
                  isError={!!aggregateErrors.discountRate}
                  text={aggregateErrors.discountRate}
                />
              )}
            </div>
            <div className="box-border border border-semantic-system-error-primary rounded-[8px] w-[572px] h-[90px] bg-semantic-system-error-10 flex justify-center flex-col py-[16px] px-[12px] gap-[12px]">
              <H5 className="text-semantic-system-error-text">주의하세요!</H5>
              <Body14Regular className="text-semantic-system-error-text">
                등업 기준은 참고치이며, 관리자가 파트너계정별로 등급을 수동
                업데이트해야 합니다
              </Body14Regular>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-[16px]">
          {selectedTier && (
            <Button color={ButtonColor.RED} onClick={handleDeleteClick}>
              부서 삭제하기
            </Button>
          )}
          <Button
            disabled={disabled}
            color={disabled ? ButtonColor.DISABLED : ButtonColor.PRIMARY}
            onClick={selectedTier ? handleUpdateClick : handleCreateClick}
          >
            {selectedTier ? "수정하기" : "추가하기"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
